import './ShopDetails.css';
import React, { useState } from 'react';
import { deleteShop } from '../../../model/shop';
import { useRouterParam, history } from '../../../model/history';
import { useMeta, getPageTitle } from '../../../model/meta';
import { dig, formatCurrency, useReloadKey } from '../../../util/util';
import { shopsPath } from '../../../routes';
import { Container } from '../../../components/Container/Container';
import { Card } from '../../../components/Card/Card';
import { LoadingPage } from '../../../components/LoadingPage/LoadingPage';
import { KeyValueTable } from '../../../components/KeyValueTable/KeyValueTable';
import { ShopForm } from './ShopCard/ShopForm';
import { PermissionButton } from '../../../components/Button/PermissionButton';
import { Icon } from '../../../components/Icon/Icon';
import { useConfirmation } from '../../../components/ConfirmationPrompt/ConfirmationPrompt';
import { Tag } from '../../../components/Tag/Tag';
import { t } from '../../../locales/translate';
import { Stack } from '../../../components/Stack/Stack';
import { paymentMethodsById } from '../../../model/payment';
import { Modal } from '../../../components/Modal/Modal';
import { Activities, ActivitiesContext } from '../../../components/Activities/Activities';
import { CustomSenderAddressCard } from './CustomSenderAddressCard/CustomSenderAddressCard';
import { CustomEmailSenderCard } from './CustomEmailSenderCard/CustomEmailSenderCard';

import { Tooltip } from '../../../components/Tooltip/Tooltip';
import { ShopOwnerships } from '../../shop_ownerships/shop_ownerships';

import { graphql } from '../../../generated';
import { useQueryHandler } from '../../../model/graphql';
import { Meta } from '../../../components/meta/meta';

const SHOP = graphql(`
  query Shop($id: ID!) {
    shop(id: $id) {
      id
      name
      slug
      domain
      logo {
        id
        url(width: 300)
        tags
      }
      defaultLocale
      paymentBlacklist
      waitingQueue
      sitemap
      orderMergingAllowed
      freeShippingThreshold
      meta

      permissions {
        id
        update
        destroy
        updateShopOwnership
      }
    }
  }
`);

export function ShopDetails(): JSX.Element {
  const id = useRouterParam<{ id: string }>().id;
  const [activitiesKey, reloadActivities] = useReloadKey();

  const { data, loading, error, refetch } = useQueryHandler(SHOP, { variables: { id } });
  const shop = data?.shop;

  const [displayModal, setDisplayModal] = useState(false);

  useMeta({
    title: getPageTitle(loading, shop?.name, shop),
    breadcrumbs: [
      { title: t.model.shop._name, url: shopsPath() },
      {
        title: getPageTitle(loading, dig(shop?.name), shop),
      },
    ],
  });

  const { confirm: showDeleteConfirm } = useConfirmation({
    message: t.general.confirmDelete(t.model.shop._name),
    action: handleShopDeleteSubmit,
  });

  async function handleShopDeleteSubmit(): Promise<void> {
    if (!shop) return;
    await deleteShop(shop.id);
    history.push(shopsPath());
  }

  if (loading) {
    return <LoadingPage title={t.actions.loading(t.model.shop._name)} />;
  } else if (error || !shop) {
    return <Container>{t.errors.itemNotFound(t.model.shop._name)}</Container>;
  }

  return (
    <ActivitiesContext.Provider value={{ reloadActivities }}>
      <Container className="shop-details">
        <Stack size="lg">
          <Card
            elevation={1}
            title=" "
            actions={
              <>
                <PermissionButton name="trash" model={shop} permission="destroy" onClick={showDeleteConfirm} hideLock />
                {shop.permissions.updateShopOwnership && (
                  <Tooltip
                    position="bottom"
                    disableRepositioning={false}
                    reference={<Icon name="users" />}
                    arrow
                    elevated
                    gap="medium"
                    mode="click"
                  >
                    <ShopOwnerships shopId={shop.id} />
                  </Tooltip>
                )}
              </>
            }
            footer=" "
          >
            <div className="shop-details__details">
              <h1 className="shop-details__name">{shop.name}</h1>
              <KeyValueTable
                items={[
                  {
                    key: t.general.slug,
                    value: shop.slug,
                  },
                  {
                    key: t.model.shop.domain,
                    value: shop.domain,
                  },
                ]}
              />
            </div>
          </Card>
          <Card
            elevation={1}
            title={t.general.generalInformation}
            actions={
              <PermissionButton
                name="edit"
                model={shop}
                permission="update"
                onClick={() => setDisplayModal(true)}
                hideLock
              />
            }
          >
            <div className="shop-details__details">
              <div className="shop-details__logo-container">
                {shop.logo && <img src={shop.logo.url} alt={shop.name} className="shop-details__logo" />}
              </div>
              <KeyValueTable
                items={[
                  {
                    key: t.model.shop.defaultLocale,
                    value: shop.defaultLocale,
                  },
                  {
                    key: t.model.shop.paymentBlacklist,
                    value: shop.paymentBlacklist?.map((payment) => (
                      <Tag key={payment} style={{ marginRight: '8px' }} text={paymentMethodsById[payment]?.name} />
                    )),
                  },
                  {
                    key: t.model.shop.waitingQueue,
                    value: shop.waitingQueue ? t.general.yes : t.general.no,
                  },
                  {
                    key: t.model.shop.siteMap,
                    value: shop.sitemap ? t.general.yes : t.general.no,
                  },
                  {
                    key: t.model.shop.orderMergingAllowed,
                    value: shop.orderMergingAllowed ? t.general.yes : t.general.no,
                  },
                  {
                    key: t.model.shop.freeShippingThreshold,
                    value: formatCurrency(shop.freeShippingThreshold),
                  },
                ]}
              />
            </div>
          </Card>
          <Meta id={shop.id} type="shop" />
          <CustomSenderAddressCard shopId={shop.id} />
          <CustomEmailSenderCard shopId={shop.id} />
          <Activities subjectType="shop" subjectId={shop.id} cacheKey={activitiesKey} />
        </Stack>

        <Modal open={displayModal} title={t.actions.edit(t.model.shop._name)} size="small">
          <ShopForm
            shop={shop}
            onClose={() => setDisplayModal(false)}
            onSave={() => {
              reloadActivities();
              refetch();
            }}
          />
        </Modal>
      </Container>
    </ActivitiesContext.Provider>
  );
}
